import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FONT, COLOR } from "../lib/tokens";
import { supabase } from "../lib/supabase";
import { usePhoneDirectory } from "../lib/usePhoneDirectory";
import { normalizeToE164 } from "../lib/phoneHash";
import FindFriendsSheet from "./FindFriendsSheet";

// SocialPage — full-screen overlay that owns every relationship-shaped
// surface that used to live (and bloat) the Settings screen:
//
//   • Identity     — username (editable), invite code, share link
//   • Discovery    — phone opt-in, find-from-contacts launcher,
//                    add-by-code form
//   • Roster       — incoming, family, friends, outgoing
//
// Mounted from UserProfile (self-view) via the new "FAMILY & FRIENDS"
// button. Same data hook (useRelationships) the old Settings copy used.

export default function SocialPage({
  userId,
  profile,
  upsertProfile,
  relationships,
  onClose,
  onOpenProfile,
}) {
  // ── identity / share ───────────────────────────────────────────
  const inviteCode = profile?.invite_code || "—";
  const inviteUrl = profile?.invite_code
    ? `https://booda.app/invite/${profile.invite_code}`
    : "";
  const inviteText = profile?.name
    ? `Hey — it's ${profile.name.split(/\s+/)[0]}. I'm using Booda for our kitchen + meal planning, join me: ${inviteUrl}`
    : `Join me on Booda: ${inviteUrl}`;
  const [copied, setCopied] = useState(false);
  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(inviteCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };
  const shareInvite = async () => {
    if (!inviteUrl) return;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Join me on Booda", text: inviteText, url: inviteUrl });
        return;
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
    }
    try {
      await navigator.clipboard.writeText(inviteText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  // ── username (editable) ────────────────────────────────────────
  const [handleDraft, setHandleDraft] = useState(profile?.username || "");
  const [handleState, setHandleState] = useState("clean"); // clean | invalid | taken | available | saving | saved
  useEffect(() => { setHandleDraft(profile?.username || ""); }, [profile?.username]);
  useEffect(() => {
    const trimmed = handleDraft.trim().toLowerCase();
    if (!trimmed || trimmed === profile?.username) { setHandleState("clean"); return; }
    if (!/^[a-z0-9_]{3,24}$/.test(trimmed)) { setHandleState("invalid"); return; }
    setHandleState("checking");
    const t = setTimeout(async () => {
      try {
        const { data } = await supabase.rpc("username_available", { p_handle: trimmed });
        setHandleState(data ? "available" : "taken");
      } catch { setHandleState("clean"); }
    }, 350);
    return () => clearTimeout(t);
  }, [handleDraft, profile?.username]);
  const saveHandle = async () => {
    const trimmed = handleDraft.trim().toLowerCase();
    if (!/^[a-z0-9_]{3,24}$/.test(trimmed)) return;
    setHandleState("saving");
    try {
      await upsertProfile({ username: trimmed });
      setHandleState("saved");
      setTimeout(() => setHandleState("clean"), 1500);
    } catch (e) {
      console.error("[social] saveHandle:", e);
      setHandleState("invalid");
    }
  };

  // ── phone opt-in ───────────────────────────────────────────────
  const phoneDir = usePhoneDirectory(userId);
  const [phoneDraft, setPhoneDraft] = useState("");
  const [phoneSaved, setPhoneSaved] = useState(false);
  const phoneOnFile = !!profile?.phone_hash;
  const phoneNormalized = normalizeToE164(phoneDraft);
  const savePhone = async () => {
    if (!phoneNormalized) return;
    const ok = await phoneDir.setMyPhone(phoneNormalized);
    if (ok) {
      setPhoneDraft("");
      setPhoneSaved(true);
      setTimeout(() => setPhoneSaved(false), 1500);
    }
  };
  const clearPhone = async () => { await phoneDir.setMyPhone(null); setPhoneDraft(""); };

  // ── add by code ────────────────────────────────────────────────
  const [code, setCode] = useState("");
  const [kind, setKind] = useState("family");
  const [busy, setBusy] = useState(false);
  const [addError, setAddError] = useState(null);
  const sendInvite = async () => {
    if (!code.trim()) return;
    setBusy(true);
    setAddError(null);
    try {
      await relationships.invite(code, kind);
      setCode("");
    } catch (e) {
      setAddError(e?.message || "Couldn't send invite");
    } finally {
      setBusy(false);
    }
  };

  // ── find friends launcher ──────────────────────────────────────
  const [findFriendsOpen, setFindFriendsOpen] = useState(false);

  // ── inline username search ─────────────────────────────────────
  // Promoted out of the FindFriendsSheet's tab so it's visible the
  // moment a user lands on the page — typing a handle is the most
  // direct path from "I have a friend's @" to "request sent."
  const [usernameQuery, setUsernameQuery] = useState("");
  const [usernameMatch, setUsernameMatch] = useState(null);
  const [usernameStatus, setUsernameStatus] = useState("idle"); // idle | searching | notFound | found | sending | sent
  const [usernameError, setUsernameError] = useState(null);
  const searchUsername = async (e) => {
    e?.preventDefault?.();
    const q = usernameQuery.trim().toLowerCase().replace(/^@/, "");
    if (!q) return;
    setUsernameStatus("searching");
    setUsernameError(null);
    setUsernameMatch(null);
    try {
      const { data, error } = await supabase.rpc("match_usernames", { p_handles: [q] });
      if (error) throw error;
      const row = (data || [])[0];
      if (!row) { setUsernameStatus("notFound"); return; }
      if (row.user_id === userId) {
        setUsernameError("That's your own handle.");
        setUsernameStatus("idle");
        return;
      }
      setUsernameMatch(row);
      setUsernameStatus("found");
    } catch (err) {
      setUsernameError(err?.message || "Couldn't look that up");
      setUsernameStatus("idle");
    }
  };
  const inviteFromUsername = async (kind) => {
    if (!usernameMatch?.invite_code) return;
    setUsernameStatus("sending");
    try {
      await relationships.invite(usernameMatch.invite_code, kind);
      setUsernameStatus("sent");
      setTimeout(() => {
        setUsernameQuery("");
        setUsernameMatch(null);
        setUsernameStatus("idle");
      }, 1400);
    } catch (err) {
      setUsernameError(err?.message || "Couldn't send invite");
      setUsernameStatus("found");
    }
  };

  const { family, friends, incoming, outgoing, accept, remove, upgradeToFamily, downgradeToFriend } = relationships;

  return createPortal(
    <div style={{
      position:"fixed", inset:0, background:COLOR.night,
      zIndex:1000, overflowY:"auto",
      WebkitOverflowScrolling:"touch",
    }}>
      <div style={{ maxWidth:560, margin:"0 auto", padding:"calc(20px + env(safe-area-inset-top)) 20px 80px" }}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24 }}>
          <button
            onClick={onClose}
            style={{ background:COLOR.soil, border:`1px solid ${COLOR.border}`, color:COLOR.dim, borderRadius:20, padding:"8px 14px", fontFamily:FONT.mono, fontSize:10, letterSpacing:"0.14em", cursor:"pointer" }}
          >
            ← BACK
          </button>
          <div style={{ fontFamily:FONT.mono, fontSize:10, color:COLOR.gold, letterSpacing:"0.18em" }}>
            FAMILY &amp; FRIENDS
          </div>
          <div style={{ width:60 }} />
        </div>

        <h1 style={{
          fontFamily:FONT.itemTitle, fontStyle:"italic", fontWeight:500,
          fontSize:34, color:COLOR.ink, marginBottom:24, lineHeight:1.1,
        }}>
          Your people
        </h1>

        {/* USERNAME */}
        <SectionHeader>YOUR HANDLE</SectionHeader>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
          <div style={{ flex:1, display:"flex", alignItems:"center", padding:"0 14px", borderRadius:12, background:COLOR.soil, border:`1.5px solid ${handleState === "available" ? COLOR.gold : handleState === "taken" || handleState === "invalid" ? COLOR.rose : COLOR.border}` }}>
            <span style={{ fontFamily:FONT.mono, fontSize:16, color:COLOR.muted }}>@</span>
            <input
              type="text"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={handleDraft}
              onChange={(e) => setHandleDraft(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 24))}
              placeholder="yourname"
              style={{ flex:1, padding:"14px 4px", border:"none", background:"transparent", color:COLOR.ink, fontFamily:FONT.mono, fontSize:16, outline:"none" }}
            />
          </div>
          <button
            onClick={saveHandle}
            disabled={handleState !== "available" && handleState !== "saving"}
            style={pillBtn(handleState === "available")}
          >
            {handleState === "saving" ? "…" : handleState === "saved" ? "SAVED" : "SAVE"}
          </button>
        </div>
        <Hint>
          {handleState === "checking" && "Checking…"}
          {handleState === "available" && `@${handleDraft} is available ✓`}
          {handleState === "taken" && `@${handleDraft} is taken — try another`}
          {handleState === "invalid" && "3–24 chars, lowercase letters / numbers / underscore"}
          {handleState === "clean" && (profile?.username ? `Friends without your phone can find you by @${profile.username}.` : "Pick a public handle so friends can find you without sharing phone numbers.")}
        </Hint>

        {/* INVITE LINK */}
        <SectionHeader>SHARE LINK</SectionHeader>
        <div style={{ background:COLOR.ground, border:"1px solid #2a2015", borderRadius:14, padding:"16px", marginBottom:10 }}>
          <div style={{ fontFamily:FONT.mono, fontSize:22, color:COLOR.gold, letterSpacing:"0.18em", fontWeight:600 }}>
            {inviteCode}
          </div>
          <div style={{ fontFamily:FONT.sans, fontSize:11, color:COLOR.muted, marginTop:4 }}>
            Six-character code. Pair it with a tap on SHARE INVITE to send a one-tap link.
          </div>
          <div style={{ display:"flex", gap:8, marginTop:12 }}>
            <button onClick={copyCode} style={ghostBtn}>
              {copied ? "COPIED ✓" : "COPY CODE"}
            </button>
            <button onClick={shareInvite} disabled={!inviteUrl} style={primaryBtn(!!inviteUrl)}>
              SHARE INVITE →
            </button>
          </div>
        </div>

        {/* PHONE OPT-IN */}
        <SectionHeader>FIND-BY-PHONE</SectionHeader>
        <div style={{ display:"flex", gap:8, marginBottom:6 }}>
          <input
            value={phoneDraft}
            onChange={(e) => setPhoneDraft(e.target.value)}
            placeholder={phoneOnFile ? "Change number…" : "Your phone (+1 415 555 1212)"}
            inputMode="tel"
            autoComplete="tel"
            style={{ flex:1, padding:"14px 16px", background:COLOR.soil, border:`1px solid ${COLOR.border}`, borderRadius:12, fontFamily:FONT.mono, fontSize:14, color:COLOR.ink, outline:"none" }}
          />
          <button onClick={savePhone} disabled={!phoneNormalized || phoneDir.busy} style={pillBtn(!!phoneNormalized)}>
            {phoneSaved ? "SAVED" : phoneDir.busy ? "…" : "SAVE"}
          </button>
        </div>
        <Hint>
          {phoneOnFile
            ? <>Your number is set — anyone who has you in their contacts can find you. <button type="button" onClick={clearPhone} style={inlineLink}>Clear it.</button></>
            : "We hash on the device; the raw digits never reach our servers."}
        </Hint>

        {/* INLINE USERNAME SEARCH */}
        <SectionHeader>SEARCH BY @USERNAME</SectionHeader>
        <form onSubmit={searchUsername} style={{ display:"flex", gap:8, marginBottom:10 }}>
          <div style={{ flex:1, display:"flex", alignItems:"center", padding:"0 14px", borderRadius:12, background:COLOR.soil, border:`1px solid ${COLOR.border}` }}>
            <span style={{ fontFamily:FONT.mono, fontSize:16, color:COLOR.muted }}>@</span>
            <input
              type="text"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={usernameQuery}
              onChange={(e) => { setUsernameQuery(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 24)); setUsernameMatch(null); setUsernameStatus("idle"); setUsernameError(null); }}
              placeholder="theirhandle"
              style={{ flex:1, padding:"14px 4px", border:"none", background:"transparent", color:COLOR.ink, fontFamily:FONT.mono, fontSize:16, outline:"none" }}
            />
          </div>
          <button
            type="submit"
            disabled={!usernameQuery.trim() || usernameStatus === "searching" || usernameStatus === "sending"}
            style={pillBtn(!!usernameQuery.trim() && usernameStatus !== "searching" && usernameStatus !== "sending")}
          >
            {usernameStatus === "searching" ? "…" : "FIND"}
          </button>
        </form>
        {usernameStatus === "notFound" && (
          <Hint>No one with @{usernameQuery} on Booda yet. Share your invite link with them instead.</Hint>
        )}
        {usernameError && <Hint><span style={{ color:COLOR.rose }}>{usernameError}</span></Hint>}
        {usernameMatch && (usernameStatus === "found" || usernameStatus === "sending" || usernameStatus === "sent") && (
          <div style={{ padding:"12px 14px", background:COLOR.ground, border:`1px solid ${COLOR.edge}`, borderRadius:12, marginBottom:16 }}>
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:10 }}>
              {usernameMatch.avatar_url ? (
                <img src={usernameMatch.avatar_url} alt="" referrerPolicy="no-referrer" style={{ width:40, height:40, borderRadius:20, objectFit:"cover" }} />
              ) : (
                <div style={{ width:40, height:40, borderRadius:20, background:COLOR.goldDeep, color:COLOR.gold, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:FONT.serif, fontSize:18 }}>
                  {(usernameMatch.name || "?")[0]?.toUpperCase()}
                </div>
              )}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontFamily:FONT.sans, fontSize:14, color:COLOR.ink }}>{usernameMatch.name || "Booda user"}</div>
                <div style={{ fontFamily:FONT.mono, fontSize:10, color:COLOR.muted, marginTop:2 }}>@{usernameMatch.username}</div>
              </div>
            </div>
            {usernameStatus === "sent" ? (
              <div style={{ fontFamily:FONT.mono, fontSize:11, color:COLOR.leaf, textAlign:"center", padding:"10px 0", letterSpacing:"0.08em" }}>
                INVITE SENT ✓
              </div>
            ) : (
              <div style={{ display:"flex", gap:8 }}>
                <button
                  type="button"
                  disabled={usernameStatus === "sending"}
                  onClick={() => inviteFromUsername("family")}
                  style={{ flex:1, padding:"10px", borderRadius:10, border:"none", background:COLOR.leaf, color:"#fff", fontFamily:FONT.mono, fontSize:10, fontWeight:600, letterSpacing:"0.08em", cursor:"pointer", opacity: usernameStatus === "sending" ? 0.6 : 1 }}
                >
                  + FAMILY
                </button>
                <button
                  type="button"
                  disabled={usernameStatus === "sending"}
                  onClick={() => inviteFromUsername("friend")}
                  style={{ flex:1, padding:"10px", borderRadius:10, border:`1px solid ${COLOR.border}`, background:COLOR.ground, color:COLOR.ink, fontFamily:FONT.mono, fontSize:10, fontWeight:600, letterSpacing:"0.08em", cursor:"pointer", opacity: usernameStatus === "sending" ? 0.6 : 1 }}
                >
                  + FRIEND
                </button>
              </div>
            )}
          </div>
        )}

        {/* FIND FROM CONTACTS */}
        <button
          type="button"
          onClick={() => setFindFriendsOpen(true)}
          style={{ width:"100%", padding:"16px", background:COLOR.leaf, color:"#fff", border:"none", borderRadius:14, fontFamily:FONT.mono, fontSize:12, fontWeight:600, letterSpacing:"0.1em", cursor:"pointer", marginBottom:30 }}
        >
          FIND FROM MY CONTACTS →
        </button>

        {/* ADD BY CODE */}
        <SectionHeader>OR ADD BY CODE</SectionHeader>
        <input
          value={code}
          onChange={e => { setCode(e.target.value.toUpperCase()); setAddError(null); }}
          placeholder="THEIR CODE"
          maxLength={8}
          style={{ width:"100%", boxSizing:"border-box", padding:"14px 16px", background:COLOR.soil, border:`1px solid ${COLOR.border}`, borderRadius:12, fontFamily:FONT.mono, fontSize:16, color:COLOR.ink, outline:"none", letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:10 }}
        />
        <div style={{ display:"flex", gap:0, padding:3, background:COLOR.deep, border:`1px solid ${COLOR.edge}`, borderRadius:10, marginBottom:10 }}>
          <button
            onClick={() => setKind("family")}
            style={tabBtn(kind === "family", COLOR.leafMuted)}
          >
            FAMILY · PANTRY + PLAN
          </button>
          <button
            onClick={() => setKind("friend")}
            style={tabBtn(kind === "friend", "#77a3d9")}
          >
            FRIEND · PREFS
          </button>
        </div>
        <button
          onClick={sendInvite}
          disabled={busy || !code.trim()}
          style={{ width:"100%", padding:"14px", background: busy || !code.trim() ? COLOR.soil : COLOR.gold, color: busy || !code.trim() ? COLOR.sub : COLOR.night, border:"none", borderRadius:12, fontFamily:FONT.mono, fontSize:12, fontWeight:600, letterSpacing:"0.08em", cursor: busy || !code.trim() ? "not-allowed" : "pointer", marginBottom:24 }}
        >
          {busy ? "SENDING…" : "SEND INVITE →"}
        </button>
        {addError && (
          <div style={{ fontFamily:FONT.sans, fontSize:12, color:COLOR.rose, marginBottom:20, padding:"10px 12px", background:COLOR.roseDeep, border:"1px solid #3a1e1e", borderRadius:8 }}>
            {addError}
          </div>
        )}

        {/* ROSTERS */}
        {incoming.length > 0 && (
          <>
            <SectionHeader>{`INCOMING (${incoming.length})`}</SectionHeader>
            {incoming.map(row => (
              <RosterRow key={row.id} row={row} onOpenProfile={onOpenProfile} actions={
                <>
                  <SmallBtn onClick={() => accept(row.id)}>ACCEPT</SmallBtn>
                  <SmallBtn onClick={() => remove(row.id)} color={COLOR.rose}>SKIP</SmallBtn>
                </>
              } />
            ))}
          </>
        )}

        <SectionHeader>{`FAMILY (${family.length})`}</SectionHeader>
        {family.length === 0 ? (
          <Empty>No family yet. Tap FIND PEOPLE or share your code.</Empty>
        ) : family.map(row => (
          <RosterRow key={row.id} row={row} onOpenProfile={onOpenProfile} actions={
            <>
              <GhostBtn onClick={() => downgradeToFriend(row.id)}>→ FRIEND</GhostBtn>
              <SmallBtn onClick={() => remove(row.id)} color={COLOR.rose}>REMOVE</SmallBtn>
            </>
          } />
        ))}

        <SectionHeader>{`FRIENDS (${friends.length})`}</SectionHeader>
        {friends.length === 0 ? (
          <Empty>No friends added yet.</Empty>
        ) : friends.map(row => (
          <RosterRow key={row.id} row={row} onOpenProfile={onOpenProfile} actions={
            <>
              <GhostBtn onClick={() => upgradeToFamily(row.id)}>→ FAMILY</GhostBtn>
              <SmallBtn onClick={() => remove(row.id)} color={COLOR.rose}>REMOVE</SmallBtn>
            </>
          } />
        ))}

        {outgoing.length > 0 && (
          <>
            <SectionHeader>{`AWAITING REPLY (${outgoing.length})`}</SectionHeader>
            {outgoing.map(row => (
              <RosterRow key={row.id} row={row} onOpenProfile={onOpenProfile} actions={
                <SmallBtn onClick={() => remove(row.id)} color={COLOR.rose}>CANCEL</SmallBtn>
              } />
            ))}
          </>
        )}
      </div>

      <FindFriendsSheet
        open={findFriendsOpen}
        onClose={() => setFindFriendsOpen(false)}
        userId={userId}
        invite={relationships.invite}
        // Without these, the invite phase is unreachable from this
        // persistent entry point: zero contact matches would show
        // "share your invite link" copy with the share button hidden
        // (it gates on inviteCode).
        inviteCode={profile?.invite_code || null}
        viewerName={profile?.name?.split(/\s+/)[0] || ""}
        existingIds={new Set([
          ...family.map(r => r.otherId),
          ...friends.map(r => r.otherId),
          ...incoming.map(r => r.otherId),
          ...outgoing.map(r => r.otherId),
        ])}
      />
    </div>,
    document.body,
  );
}

// ── shared bits ─────────────────────────────────────────────────
function SectionHeader({ children }) {
  return (
    <div style={{ fontFamily:FONT.mono, fontSize:10, color:COLOR.gold, letterSpacing:"0.16em", margin:"22px 0 10px", textTransform:"uppercase" }}>
      {children}
    </div>
  );
}
function Hint({ children }) {
  return (
    <div style={{ fontFamily:FONT.sans, fontSize:11, color:COLOR.muted, lineHeight:1.5, marginBottom:16 }}>
      {children}
    </div>
  );
}
function Empty({ children }) {
  return (
    <div style={{ fontFamily:FONT.sans, fontSize:12, color:COLOR.muted, fontStyle:"italic", padding:"14px 16px", background:COLOR.deep, border:`1px dashed ${COLOR.edge}`, borderRadius:12, marginBottom:8 }}>
      {children}
    </div>
  );
}
function SmallBtn({ onClick, color = COLOR.gold, children }) {
  return (
    <button
      onClick={onClick}
      style={{ background:"transparent", border:`1px solid ${color}`, color, borderRadius:8, padding:"6px 10px", fontFamily:FONT.mono, fontSize:10, fontWeight:600, letterSpacing:"0.08em", cursor:"pointer" }}
    >
      {children}
    </button>
  );
}
function GhostBtn({ onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{ background:"transparent", border:`1px solid ${COLOR.border}`, color:COLOR.muted, borderRadius:8, padding:"6px 10px", fontFamily:FONT.mono, fontSize:10, fontWeight:600, letterSpacing:"0.08em", cursor:"pointer" }}
    >
      {children}
    </button>
  );
}

const pillBtn = (active) => ({
  padding:"0 16px", borderRadius:12, border:"none",
  background: active ? COLOR.gold : COLOR.soil,
  color: active ? COLOR.night : COLOR.sub,
  fontFamily:FONT.mono, fontSize:11, fontWeight:600, letterSpacing:"0.08em",
  cursor: active ? "pointer" : "not-allowed",
});
const ghostBtn = {
  flex:1, padding:"10px", borderRadius:10, border:`1px solid ${COLOR.border}`,
  background:"transparent", color:COLOR.ink,
  fontFamily:FONT.mono, fontSize:11, fontWeight:600, letterSpacing:"0.08em", cursor:"pointer",
};
const primaryBtn = (active) => ({
  flex:2, padding:"10px", borderRadius:10, border:"none",
  background: active ? COLOR.gold : COLOR.soil,
  color: active ? COLOR.night : COLOR.sub,
  fontFamily:FONT.mono, fontSize:11, fontWeight:600, letterSpacing:"0.08em",
  cursor: active ? "pointer" : "not-allowed",
});
const tabBtn = (active, fg) => ({
  flex:1, padding:"8px",
  background: active ? COLOR.edge : "transparent",
  border:"none", borderRadius:7,
  fontFamily:FONT.mono, fontSize:10, fontWeight:600,
  color: active ? fg : COLOR.muted, letterSpacing:"0.08em", cursor:"pointer",
});
const inlineLink = {
  background:"none", border:"none", padding:0,
  color:COLOR.rose, fontFamily:FONT.mono, fontSize:11, cursor:"pointer", textDecoration:"underline",
};

function RosterRow({ row, actions, onOpenProfile }) {
  const name = row.other?.name || "Unknown";
  const initial = name[0]?.toUpperCase() || "?";
  const handle = row.other?.username;
  const canOpen = !!(onOpenProfile && row.status === "accepted" && row.otherId);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 14px", background:COLOR.ground, border:`1px solid ${COLOR.edge}`, borderRadius:12, marginBottom:8 }}>
      <button
        onClick={canOpen ? () => onOpenProfile(row.otherId) : undefined}
        disabled={!canOpen}
        style={{ display:"flex", alignItems:"center", gap:12, flex:1, minWidth:0, background:"transparent", border:"none", padding:0, textAlign:"left", cursor: canOpen ? "pointer" : "default" }}
      >
        {row.other?.avatar_url ? (
          <img src={row.other.avatar_url} alt={name} referrerPolicy="no-referrer" style={{ width:40, height:40, borderRadius:20, objectFit:"cover", flexShrink:0 }} />
        ) : (
          <div style={{ width:40, height:40, borderRadius:20, background:COLOR.goldDeep, color:COLOR.gold, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:FONT.serif, fontSize:18, fontWeight:500, flexShrink:0 }}>
            {initial}
          </div>
        )}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontFamily:FONT.sans, fontSize:14, color:COLOR.ink, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
            {name}
          </div>
          {handle && (
            <div style={{ fontFamily:FONT.mono, fontSize:10, color:COLOR.muted, letterSpacing:"0.04em", marginTop:2 }}>
              @{handle}
            </div>
          )}
        </div>
      </button>
      <div style={{ display:"flex", gap:6, flexShrink:0 }}>
        {actions}
      </div>
    </div>
  );
}
